import React, { useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { LinkedinIcon, LinkedinShareButton } from 'react-share';

import { ContentLayout } from 'components/Layout';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { paths } from 'routes';
import { DELEGON_URL } from 'configuration';
import { useGetCompanyById } from '../api/getCompanyById';
import { DetailViewTabList } from 'components/TabList';
import { FrameworkAgreementsTab } from '../components/FrameworkAgreementsTab';
import { DirectsTab } from '../components/DirectsTab';
import { DPSTab } from '../components/DPSTab';
import { CompaniesTab } from '../components/CompaniesTab';
import { StatusToggleButton } from '../components/StatusToggleButton';
import { ValidateToggleButton } from '../components/ValidateToggleButton';
import { AccordionButton, AccordionListItem } from 'components/Button';
import { useAuth } from 'lib/auth';
import { useCompanyFrameworkAgreementsCount } from '../api/getCompanyFrameworkAgreementsCount';
import { BannerImage } from '../components';
import { SquareImage } from '../components/SquareImage';
import { LogoUpload } from '../components/LogoUpload';
import { Subscription } from '../components/Subscription';
import { EditCompanyModal } from '../components/EditCompanyModal';
import { useRelatedDirectsCount } from '../api/getRelatedDirectsCount';
import { useCompanyDPSCount } from '../api/getRelatedDPSCount';
import { Loader } from 'components/Loader';
import { DetailsTab } from '../components/DetailsTab';
import { SettingsTab } from '../components/SettingsTab';
import { EditForm } from '../components/EditCompanyProfile';
import { useGetCompanyBySlug } from '../api/getCompanyBySlug';
import { useTrackVisit } from 'hook/useTrackVisit';

export const DetailsView = ({ param = 'id' }) => {
  const { account, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const currentTab = queryParams.get('tab') || 'details';
  const { id: idParam, type: slugType, slug } = useParams();

  const { data: companyBySlug } = useGetCompanyBySlug(slugType, slug, {
    enabled: param === 'slug',
  });

  const id = param === 'slug' ? companyBySlug?.company_id : idParam;

  const {
    data: { company = {}, company_profile = {} } = {},
    refetch,
    isLoading,
    isFetching,
  } = useGetCompanyById(id, {
    enabled: !!id,
  });
  useTrackVisit('company_profile', company.company_id, 'app');

  const { data: { count: activeFACount } = {} } = useCompanyFrameworkAgreementsCount(id, {
    enabled: !!id,
  });
  const { data: { count: expiredFACount } = {} } = useCompanyFrameworkAgreementsCount(id, {
    expired: true,
    enabled: !!id,
  });

  const { data: { count: directsCount } = {} } = useRelatedDirectsCount(id, {
    onlyExpired: false,
    enabled: !!id,
  });
  const { data: { count: expiredDirectsCount } = {} } = useRelatedDirectsCount(id, {
    onlyExpired: true,
    enabled: !!id,
  });

  const { data: { count: dpsCount } = {} } = useCompanyDPSCount(id, {
    enabled: !!id,
  });
  const { data: { count: expiredDpsCount } = {} } = useCompanyDPSCount(id, {
    expired: true,
    enabled: !!id,
  });

  const companyType = company?.Type === 'buyer' ? 'buyers' : 'suppliers';
  const showDPS = company?.Type === 'buyer' && company?.sector !== 'private';
  const canManage = account.super_admin || String(user?.Company?.company_id) === String(id);

  const shareButton = company_profile?.slug && (
    <Box sx={{ display: 'flex', '& svg': { display: 'block' } }}>
      <LinkedinShareButton url={`${DELEGON_URL}${paths.public.companyPublicProfile(company?.Type, company_profile.slug)}`}>
        <LinkedinIcon size={24} borderRadius={16} />
      </LinkedinShareButton>
    </Box>
  );

  const switchTab = (_, newTab) => {
    navigate(`?tab=${newTab}`, { replace: true });
  };

  const tabs = [
    { label: 'Details', value: 'details' },
    { label: 'Framework Agreements', value: 'agreements', count: activeFACount + expiredFACount },
    { label: 'Directs', value: 'directs', count: directsCount + expiredDirectsCount },
    ...(showDPS ? [{ label: 'DPS', value: 'dps', count: dpsCount + expiredDpsCount }] : []),
    {
      label: company?.Type === 'buyer' ? 'Suppliers' : 'Buyers',
      name: company?.Type === 'buyer' ? 'suppliers' : 'buyers',
    },
    ...(account.super_admin ? [{ label: 'Settings' }] : []),
  ];

  return (
    <ContentLayout>
      {isLoading ? (
        <Loader />
      ) : (
        company && (
          <Stack height="100%">
            <BreadCrumbs
              items={[
                { name: '', to: paths.dashboard },
                { name: companyType, to: `/app/${companyType}` },
                { name: company.Name },
              ]}
            />
            <BannerImage banner={company?.background_image_url} bannerChangable={canManage} refetch={refetch} companyId={id} />
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{
                backgroundColor: 'white',
                py: 2,
                px: 4,
              }}
            >
              <Stack direction="row" gap={2}>
                <SquareImage image={company?.square_logo_url || ''} bannerChangable={canManage} companyId={id} />
                <Box>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Typography variant="h2">{company?.Name}</Typography>
                    {account.super_admin && (
                      <Box
                        borderRadius="100%"
                        width={8}
                        height={8}
                        border="1px solid"
                        borderColor={company?.Status === 'active' ? 'success.light' : 'error.light'}
                        bgcolor={company?.Status === 'active' ? 'success.light' : 'error.light'}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {company?.VAT}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row">
                {canManage && (
                  <AccordionButton label="Actions" sx={{ width: 130 }} endAdornment={shareButton}>
                    <Stack gap={0.5}>
                      {account.super_admin && (
                        <>
                          <StatusToggleButton refetch={refetch} status={company?.Status} />
                          {company?.Type === 'supplier' && (
                            <ValidateToggleButton refetch={refetch} validated={company?.validated} />
                          )}
                          <AccordionListItem
                            onClick={() =>
                              navigate(
                                company?.Type === 'buyer'
                                  ? paths.buyersUsersView(id)
                                  : paths.suppliersUsersView(id)
                              )
                            }
                          >
                            Manage Users
                          </AccordionListItem>
                          <EditCompanyModal company={company} refetch={refetch} />
                        </>
                      )}
                      <LogoUpload companyId={id} />
                      <Subscription
                        currentLevel={company?.subscription_level}
                        isSuperAdmin={account.super_admin}
                        company={company}
                        currentInvoicePeriod={company?.invoice_period}
                      />
                      <EditForm company={{ ...company, CompanyProfile: company_profile }} refetch={refetch} />
                    </Stack>
                  </AccordionButton>
                )}
                {!canManage && shareButton && (
                  <Box sx={{ display: 'flex', alignItems: 'center', height: 32, mt: 1 }}>{shareButton}</Box>
                )}
              </Stack>
            </Stack>
            <DetailViewTabList value={currentTab} onChange={switchTab} tabs={tabs} />

            <Stack
              px={4}
              py={2}
              bgcolor="background.paper"
              borderTop="1px solid"
              borderColor="border.main"
              gap={1}
              height={1}
            >
              {currentTab === 'details' && (
                <DetailsTab currentTab={currentTab} companyProfile={company_profile} />
              )}
              {currentTab === 'agreements' && (
                <FrameworkAgreementsTab
                  id={id}
                  currentTab={currentTab}
                  activeCount={activeFACount}
                  expiredCount={expiredFACount}
                />
              )}
              {currentTab === 'directs' && (
                <DirectsTab
                  id={id}
                  companyType={company.Type}
                  currentTab={currentTab}
                  activeCount={directsCount}
                  expiredCount={expiredDirectsCount}
                />
              )}
              {currentTab === 'dps' && showDPS && (
                <DPSTab
                  id={id}
                  currentTab={currentTab}
                  activeCount={dpsCount}
                  expiredCount={expiredDpsCount}
                />
              )}
              {(currentTab === 'suppliers' || currentTab === 'buyers') && (
                <CompaniesTab id={id} currentTab={currentTab} companyType={company.Type} />
              )}
              {currentTab === 'settings' && account.super_admin && (
                <SettingsTab companyId={id} companyName={company.Name} companyType={company.Type} />
              )}
            </Stack>
          </Stack>
        )
      )}
    </ContentLayout>
  );
};
